// DJ 정보를 파싱하는 유틸리티 함수

/**
 * DJ 정보를 파싱해서 인스타그램 링크와 함께 반환
 * @param {string} description - 이벤트 설명
 * @returns {Array} DJ 정보 배열 [{ name, instagram, handle }]
 */
export const parseDJsFromDescription = (description) => {
  if (!description) return [];

  const djList = [];
  const lines = description.split('\n');
  
  // DJ 섹션 찾기
  let inDJSection = false;
  
  for (let line of lines) {
    const trimmedLine = line.trim();
    
    // DJ 섹션 시작 감지
    if ((trimmedLine.toLowerCase().includes('dj') && 
        (trimmedLine.toLowerCase().includes('lineup') || 
         trimmedLine === 'DJs' || 
         trimmedLine === 'DJ' ||
         trimmedLine === 'DJ LINE UP')) ||
        trimmedLine.toLowerCase().startsWith('music by') ||
        trimmedLine === 'MUSIC BY') {
      inDJSection = true;
      continue;
    }
    
    // DJ 섹션 종료 감지 (다른 섹션 시작)
    if (inDJSection && 
        (trimmedLine.toLowerCase().includes('door') ||
         trimmedLine.toLowerCase().includes('entrance') ||
         trimmedLine.toLowerCase().includes('entranece') || // 오타 케이스
         trimmedLine.toLowerCase().includes('fee') ||
         trimmedLine.toLowerCase().includes('won') ||
         trimmedLine.toLowerCase().includes('table') ||
         trimmedLine.toLowerCase().includes('reservation') ||
         trimmedLine.toLowerCase().includes('artwork') ||
         trimmedLine.toLowerCase().includes('*artwork') ||
         trimmedLine.toLowerCase().includes('info') ||
         trimmedLine.toLowerCase().includes('ticket') ||
         trimmedLine.toLowerCase().includes('price') ||
         /^\d+PM/.test(trimmedLine) || // 시간 패턴 (10PM~LATE)
         /^\d+:\d+/.test(trimmedLine) || // 시간 패턴 (21:00)
         (trimmedLine.match(/^\d+,?\d*\s*원?$/)) || // 가격 패턴 (20,000 WON)
         (trimmedLine === '' && djList.length > 0))) {
      break;
    }
    
    // DJ 정보 파싱
    if (inDJSection && trimmedLine) {
      const djInfo = parseDJLine(trimmedLine);
      if (djInfo) {
        djList.push(djInfo);
      }
    }
  }
  
  return djList;
};

/**
 * 개별 DJ 라인을 파싱
 * @param {string} line - DJ 정보가 포함된 라인
 * @returns {Object|null} DJ 정보 객체
 */
const parseDJLine = (line) => {
  // 패턴들:
  // "SORIN @sorinkeem"
  // "AKI JUN @yoonjihoonjiyoon" 
  // "UTTNG @uttng"
  // "TAB JONES @tabjonesmusic"
  // "@sorinkeem"
  // "SORIN"
  
  const patterns = [
    // "NAME @handle" 패턴 (대소문자, 숫자, 특수문자 허용)
    /^([A-Za-z0-9\s\-_]+)\s+@([a-zA-Z0-9_.]+)$/,
    // "@handle NAME" 패턴  
    /^@([a-zA-Z0-9_.]+)\s+([A-Za-z0-9\s\-_]+)$/,
    // "@handle" 패턴만
    /^@([a-zA-Z0-9_.]+)$/,
    // "NAME" 패턴만 (인스타그램 없음, 대소문자, 숫자, 특수문자 허용)
    /^([A-Za-z0-9\s\-_]+)$/
  ];
  
  for (let i = 0; i < patterns.length; i++) {
    const match = line.match(patterns[i]);
    if (match) {
      switch (i) {
        case 0: // "NAME @handle"
          return {
            name: match[1].trim(),
            handle: match[2],
            instagram: `https://instagram.com/${match[2]}`
          };
        case 1: // "@handle NAME"
          return {
            name: match[2].trim(),
            handle: match[1],
            instagram: `https://instagram.com/${match[1]}`
          };
        case 2: // "@handle"
          return {
            name: match[1],
            handle: match[1],
            instagram: `https://instagram.com/${match[1]}`
          };
        case 3: // "NAME"
          return {
            name: match[1].trim(),
            handle: null,
            instagram: null
          };
      }
    }
  }
  
  return null;
};

/**
 * DJ 이름에서 핸들 정보를 제거하고 깔끔하게 표시하기 위한 함수
 * @param {string} djName - DJ 이름 (예: "AKI JUN @yoonjihoonjiyoon")
 * @param {string} handle - Instagram 핸들
 * @returns {string} 깔끔한 DJ 이름 (예: "AKI JUN")
 */
export const getCleanDJName = (djName, handle) => {
  if (!handle) return djName;
  
  // "@handle" 부분을 제거
  const handlePattern = `@${handle}`;
  return djName.replace(handlePattern, '').trim();
};

/**
 * description에서 DJ 정보를 하이라이트된 텍스트로 변환
 * @param {string} description - 원본 설명
 * @param {Array} djList - 파싱된 DJ 목록
 * @returns {Array} 렌더링용 컴포넌트 배열
 */
export const renderDescriptionWithDJLinks = (description, djList) => {
  if (!description || djList.length === 0) {
    return description;
  }
  
  let processedText = description;
  const elements = [];
  
  // DJ 정보를 링크로 교체
  djList.forEach((dj, index) => {
    const djPattern = dj.handle 
      ? `${dj.name} @${dj.handle}`
      : dj.name;
    
    if (processedText.includes(djPattern)) {
      const parts = processedText.split(djPattern);
      elements.push(parts[0]);
      elements.push({
        type: 'dj-link',
        key: `dj-${index}`,
        name: dj.name,
        handle: dj.handle,
        instagram: dj.instagram
      });
      processedText = parts.slice(1).join(djPattern);
    }
  });
  
  if (processedText) {
    elements.push(processedText);
  }
  
  return elements;
}; 